import React, { FunctionComponent, useMemo } from 'react'
import { defineMessages, useIntl } from 'react-intl'

import styled, { useTheme } from 'styled-components/native'

import { CENTER_OF_EXCELLENCE } from '@lyrahealth-inc/shared-app-logic'

import { SubheadSize } from '../../styles'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'
import { BodyText } from '../bodyText/BodyText'
import { ArrowIcon } from '../icons/ArrowIcon'
import { NeurodiversityCOEToolkitIcon } from '../icons/NeurodiversityCOEToolkitIcon'
import { PressableOpacity } from '../pressableOpacity/PressableOpacity'
import { Subhead } from '../subhead/Subhead'

export interface CenterOfExcellenceEntrypointProps {
  centerOfExcellenceType: CENTER_OF_EXCELLENCE
  isUserTeen: boolean
  onPress: () => void
}

const centerOfExcellenceToolkitTitles = defineMessages({
  NEURODIVERSITY: {
    defaultMessage: 'Learn more about neurodiversity support',
    description: 'Title for the neurodiversity center of excellence entrypoint',
  },
})

const centerOfExcellenceToolkitDescriptions = defineMessages({
  NEURODIVERSITY_ADULT: {
    defaultMessage:
      'Expert care for neurodiverse individuals, parents, and children ages 1-17, including neuropsychological evaluations and caregiver coaching.',
    description: 'Description for the neurodiversity center of excellence entrypoint for adult users',
  },
  NEURODIVERSITY_TEEN: {
    defaultMessage: 'Access expert care for neurodiverse needs, consider neuropsychological testing, and more.',
    description: 'Description for the neurodiversity center of excellence entrypoint for teen users',
  },
})

const Container = styled.View(({ theme }) => ({
  backgroundColor: theme.colors.components.centerOfExcellenceEntryPointBackground.background,
  borderRadius: theme.spacing['16px'],
  padding: theme.spacing['16px'],
  flexDirection: 'row',
}))

const TextAndIconContainer = styled.View(({ theme }) => ({
  flex: 1,
  flexDirection: theme.breakpoints.isMinWidthTablet ? 'row' : 'column',
  alignItems: 'center',
  gap: theme.breakpoints.isMinWidthTablet ? theme.spacing['16px'] : '10px',
}))

const TextContainer = styled.View(({ theme }) => ({
  flexDirection: 'column',
  gap: theme.spacing['8px'],
  maxWidth: '728px',
  ...(theme.breakpoints.isMinWidthTablet && { flex: 1 }),
}))

const IconContainer = styled.View(({ theme }) => ({
  height: '63px',
  width: '63px',
  ...(theme.breakpoints.isMobileSized && { width: '100%' }),
}))

const ArrowContainer = styled.View({
  justifyContent: 'center',
  alignItems: 'flex-end',
})

export const CenterOfExcellenceEntrypoint: FunctionComponent<CenterOfExcellenceEntrypointProps> = ({
  centerOfExcellenceType,
  isUserTeen,
  onPress,
}) => {
  const { breakpoints, colors } = useTheme() as ThemeType
  const { formatMessage } = useIntl()
  const CENTER_OF_EXCELLENCE_TOOLKIT_TO_CONTENT = useMemo(
    () => ({
      [CENTER_OF_EXCELLENCE.NEURODIVERSITY]: {
        title: centerOfExcellenceToolkitTitles.NEURODIVERSITY,
        description: isUserTeen
          ? centerOfExcellenceToolkitDescriptions.NEURODIVERSITY_TEEN
          : centerOfExcellenceToolkitDescriptions.NEURODIVERSITY_ADULT,
        getIcon: (size?: number) => <NeurodiversityCOEToolkitIcon size={size} />,
      },
    }),
    [isUserTeen],
  )
  const { title, description, getIcon } = CENTER_OF_EXCELLENCE_TOOLKIT_TO_CONTENT[centerOfExcellenceType]
  const iconComponent = getIcon()
  return (
    <PressableOpacity onPress={onPress} testID={tID('CenterOfExcellenceEntrypoint')}>
      <Container>
        <TextAndIconContainer>
          <IconContainer>{iconComponent}</IconContainer>
          <TextContainer>
            <Subhead
              size={SubheadSize.MEDIUM}
              text={formatMessage(title)}
              numberOfLines={breakpoints.isMinWidthTablet ? 2 : 4}
            />
            <BodyText text={formatMessage(description)} />
          </TextContainer>
        </TextAndIconContainer>
        <ArrowContainer>
          <ArrowIcon fillColor={colors.textSecondary} />
        </ArrowContainer>
      </Container>
    </PressableOpacity>
  )
}
